import { Box, Button, chakra, Flex, Stack, Text } from "@chakra-ui/react";
import React from "react";
import type { SignaturePad as SignaturePadInstance } from "../../../../../public/vendors/signature_pad/signature_pad";
import { SignaturePad } from "../../../../../public/vendors/signature_pad/signature_pad";
import { bodyDrawingBaseImage } from "./blockFormat";
import {
  BODY_DRAWING_VIEWS,
  bodyDrawingViewValue,
  normalizeBodyDrawingAnswer,
  setBodyDrawingView,
  type BodyDrawingView,
} from "./bodyDrawingValue";
import { EditorShell } from "./EditorShell";
import { isAnswerChanged, useEditorDraft } from "./editorValues";
import type { QuestionEditorProps } from "./types";

// Editor of bodyDrawing: the four views (FRONT, RIGHT, BACK, LEFT), one drawn at a time on a
// canvas over its image (the stored drawing, else the PDF's default image). Like mobile's
// AnatomyCanvas, a view's value is a PNG data URL of the whole canvas: the image with the strokes
// on it. Clear puts a view back to "" (untouched: the PDF prints the default image). Save writes
// the `string[4]` in stored order through bodyDrawingValue.ts.

/** The default images' size, also mobile's largest canvas. */
const VIEW_WIDTH = 320;
const VIEW_HEIGHT = 815;
const THUMBNAIL_WIDTH = 64;

/**
 * The default images are served with CORS, so the canvas can still be exported once they are drawn
 * on it. The preview's <img> already loaded them without CORS, and the browser would reuse that
 * cached (non-CORS) response and block it: a URL of its own makes a fresh CORS request.
 */
function canvasImageUrl(url: string): string {
  if (!/^https?:\/\//i.test(url)) {
    return url;
  }
  return `${url}${url.includes("?") ? "&" : "?"}canvas=1`;
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Could not load ${url}`));
    image.src = canvasImageUrl(url);
  });
}

function BodyDrawingCanvas(props: {
  background: string;
  isDisabled: boolean;
  onChange: (dataUrl: string) => void;
  onError: (message: string | null) => void;
}) {
  const { background, onChange, onError } = props;
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const padRef = React.useRef<SignaturePadInstance | null>(null);
  const isDisabledRef = React.useRef(props.isDisabled);
  isDisabledRef.current = props.isDisabled;
  const [isReady, setIsReady] = React.useState(false);

  // Mounted once per view (the parent keys it): draw the image, then let the reviewer draw on it.
  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) {
      return;
    }
    let isCancelled = false;

    loadImage(background).then(
      (image) => {
        if (isCancelled) {
          return;
        }
        // The pad clears the canvas when created, so it comes before the image
        const pad: SignaturePadInstance = new SignaturePad(canvas, {
          onEnd: () => {
            try {
              onChange(canvas.toDataURL());
              onError(null);
            } catch {
              onError("This drawing cannot be saved from the browser. Clear the view and redraw.");
            }
          },
        });
        canvas.getContext("2d")?.drawImage(image, 0, 0, VIEW_WIDTH, VIEW_HEIGHT);
        padRef.current = pad;
        if (isDisabledRef.current) {
          pad.off();
        }
        setIsReady(true);
      },
      () => {
        if (!isCancelled) {
          onError("Could not load the body image. Check your connection and reopen the editor.");
        }
      }
    );

    return () => {
      isCancelled = true;
      padRef.current?.off();
      padRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (props.isDisabled) {
      padRef.current?.off();
    } else {
      padRef.current?.on();
    }
  }, [props.isDisabled]);

  return (
    <Box borderRadius="md" borderWidth="1px" flexShrink={0} overflow="hidden" width="fit-content">
      <canvas
        ref={canvasRef}
        height={VIEW_HEIGHT}
        style={{
          cursor: isReady && !props.isDisabled ? "crosshair" : "default",
          display: "block",
          touchAction: "none",
        }}
        width={VIEW_WIDTH}
      />
    </Box>
  );
}

function ViewThumbnail(props: {
  isSelected: boolean;
  url: string;
  view: BodyDrawingView;
  onSelect: () => void;
}) {
  return (
    <chakra.button
      aria-pressed={props.isSelected}
      borderColor={props.isSelected ? "blue.500" : "gray.200"}
      borderRadius="md"
      borderWidth="2px"
      display="flex"
      flexDirection="column"
      gap={1}
      p={1}
      type="button"
      onClick={props.onSelect}
    >
      <chakra.img alt={props.view} src={props.url} width={`${THUMBNAIL_WIDTH}px`} />
      <Text
        alignSelf="center"
        color={props.isSelected ? "blue.600" : "gray.600"}
        fontSize="xs"
        fontWeight={600}
      >
        {props.view}
      </Text>
    </chakra.button>
  );
}

export function BodyDrawingEditor(props: QuestionEditorProps) {
  const { draft, isTouched, updateDraft } = useEditorDraft(() =>
    normalizeBodyDrawingAnswer(props.value)
  );
  const [selectedView, setSelectedView] = React.useState<BodyDrawingView>("FRONT");
  // Bumped by Clear so the canvas remounts over the default image
  const [clearCount, setClearCount] = React.useState(0);
  const [canvasError, setCanvasError] = React.useState<string | null>(null);

  // Against the normalized stored answer: mobile's short arrays are the same drawing as their
  // four-item form. Comparing data URLs is costly: only when the draft changes.
  const isChanged = React.useMemo(
    () => isTouched && isAnswerChanged(normalizeBodyDrawingAnswer(props.value), draft),
    [isTouched, props.value, draft]
  );

  const viewUrl = (view: BodyDrawingView) => {
    const value = bodyDrawingViewValue(draft, view);
    return value === "" ? bodyDrawingBaseImage(view) : value;
  };
  const isSelectedDrawn = bodyDrawingViewValue(draft, selectedView) !== "";

  return (
    <EditorShell
      canSave={isChanged}
      error={props.error}
      isSaving={props.isSaving}
      label={props.question.label || "Body drawing"}
      onCancel={props.onCancel}
      onSave={() => props.onSave(draft)}
    >
      <Stack spacing={3}>
        <Text color="gray.600" fontSize="xs">
          Pick a view and draw on it. Clear removes that view&apos;s drawing.
        </Text>
        <Flex flexWrap="wrap" gap={2}>
          {BODY_DRAWING_VIEWS.map((view) => (
            <ViewThumbnail
              key={view}
              isSelected={view === selectedView}
              url={viewUrl(view)}
              view={view}
              onSelect={() => {
                setCanvasError(null);
                setSelectedView(view);
              }}
            />
          ))}
        </Flex>
        <Flex alignItems="flex-end" flexWrap="wrap" gap={3}>
          <BodyDrawingCanvas
            // One canvas per view; Clear starts it over
            key={`${selectedView}-${clearCount}`}
            background={viewUrl(selectedView)}
            isDisabled={props.isSaving}
            onChange={(dataUrl) =>
              updateDraft((prev) => setBodyDrawingView(prev, selectedView, dataUrl))
            }
            onError={setCanvasError}
          />
          <Button
            isDisabled={!isSelectedDrawn}
            size="sm"
            variant="outline"
            onClick={() => {
              updateDraft((prev) => setBodyDrawingView(prev, selectedView, ""));
              setCanvasError(null);
              setClearCount((count) => count + 1);
            }}
          >
            Clear {selectedView.toLowerCase()}
          </Button>
        </Flex>
        {canvasError !== null && (
          <Text color="red.600" fontSize="xs">
            {canvasError}
          </Text>
        )}
      </Stack>
    </EditorShell>
  );
}
